import { DocusealForm } from '@docuseal/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/components/auth/AuthProvider';
import { Button } from '@/components/ui/button';
import { Title } from '@/components/ui/custom/title';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader } from '@/components/ui/loader';
import { fetchWithSupabase } from '@/lib/api/fetch';
import { ContractsResponse } from '@/lib/api/types';
import { GET_CONTRACT_INFO } from '@/lib/api/url';

export const ContractsPage = () => {
  const { session } = useAuth();
  const { i18n, t } = useTranslation();
  const { data: contracts, isLoading } = useQuery({
    queryKey: ['contracts'],
    queryFn: async () => {
      const res = await fetchWithSupabase<ContractsResponse>(GET_CONTRACT_INFO, {
        method: 'GET',
      });
      return res.contracts_info;
    },
    initialData: [],
  });
  const queryClient = useQueryClient();
  const [selectedContract, setSelectedContract] = useState<{
    contract_slug: string;
    template_id: number;
    is_signed: boolean;
    contract_name: string;
  } | null>(null);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div>
      <Title title="Contracts" />
      <div className="mt-4 grid gap-4">
        {contracts.map(({ contract_name, contract_slug, is_signed, template_id }) => (
          <div
            className="rounded-lg border bg-white p-6 flex flex-col gap-6"
            key={contract_slug}
          >
            <div className="flex flex-col gap-2">
              <h2 className="text-lg font-semibold">{contract_name}</h2>
              <Button
                className="w-fit"
                onClick={() =>
                  setSelectedContract({
                    contract_slug,
                    template_id,
                    is_signed,
                    contract_name,
                  })
                }
              >
                {is_signed
                  ? t('registration.reviewAndSignContract.viewContract')
                  : t('registration.reviewAndSignContract.signContract')}
              </Button>
            </div>
          </div>
        ))}
      </div>
      <Dialog
        open={selectedContract !== null}
        onOpenChange={() => setSelectedContract(null)}
      >
        <DialogContent className="sm:max-w-[800px]">
          <DialogHeader>
            <DialogTitle>{selectedContract?.contract_name}</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[90vh] border rounded-md">
            <DocusealForm
              src={`https://docuseal.co/d/${selectedContract?.contract_slug}`}
              email={session?.user.email}
              language={i18n.language}
              expand={false}
              onComplete={() => {
                setSelectedContract(null);
                queryClient.invalidateQueries({ queryKey: ['contracts'] });
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
